import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, ExternalLink, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";

export const LaunchingSoonModal = ({ open, onOpenChange, pillar }) => {
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setContact("");
      setDone(false);
    }
  }, [open, pillar]);

  if (!pillar) return null;
  const Icon = pillar.icon;

  const subscribe = async () => {
    if (!contact.trim()) return toast.error("Enter your phone or email");
    setBusy(true);
    try {
      await api.post("/pillars/subscribe", { pillar: pillar.id, contact });
      setDone(true);
      toast.success(`You're on the ${pillar.short} launch list`);
    } catch (e) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="launching-soon-modal" className="sm:max-w-md p-0 overflow-hidden border-0 bg-white">
        <DialogTitle className="sr-only">{pillar.name} launching soon</DialogTitle>
        <DialogDescription className="sr-only">Join the launch list to be notified when this pillar goes live.</DialogDescription>
        <div className="kenya-stripe h-1.5" />
        <div className="relative h-36 overflow-hidden">
          <img src={pillar.image} alt={pillar.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-talanta-green via-talanta-green/60 to-transparent" />
          <div className="absolute bottom-4 left-5 flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/95 flex items-center justify-center">
              <Icon className="h-5 w-5 text-talanta-terracotta" />
            </div>
            <div className="text-white">
              <div className="text-[10px] uppercase tracking-widest text-talanta-gold font-semibold">Launching Soon</div>
              <div className="font-display font-bold text-xl">{pillar.name}</div>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          <p className="text-sm text-talanta-charcoal/70">{pillar.blurb}</p>

          {done ? (
            <div className="mt-5 flex flex-col items-center text-center py-4">
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              <p className="mt-3 font-display font-semibold text-talanta-green">You're on the list!</p>
              <p className="text-sm text-talanta-charcoal/50">We'll notify you the moment {pillar.short} goes live.</p>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              <label className="text-xs font-semibold uppercase tracking-wider text-talanta-charcoal/50">Notify me at launch</label>
              <div className="flex items-center gap-2 rounded-xl border border-talanta-green/15 bg-talanta-cream px-3 focus-within:ring-2 focus-within:ring-talanta-green">
                <Bell className="h-4 w-4 text-talanta-green/50" />
                <input
                  data-testid="pillar-subscribe-input"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="Phone or email"
                  className="flex-1 bg-transparent py-3 outline-none text-talanta-charcoal font-medium"
                  onKeyDown={(e) => e.key === "Enter" && subscribe()}
                />
              </div>
              <button
                data-testid="pillar-subscribe-btn"
                onClick={subscribe}
                disabled={busy}
                className="w-full rounded-xl bg-talanta-red hover:bg-talanta-red/90 py-3.5 text-white font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.01] disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Keep me posted"}
              </button>
            </div>
          )}

          {pillar.externalUrl && (
            <a
              data-testid="pillar-deeplink-btn"
              href={pillar.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full rounded-xl border border-talanta-green/20 py-3 text-sm font-semibold text-talanta-green hover:bg-talanta-cream flex items-center justify-center gap-2"
            >
              Visit current site <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
